/** Pipeline factories. Subsystem code calls these to keep WGSL → JS plumbing consistent. */

export function createComputePipeline(
  device: GPUDevice,
  wgsl: string,
  entryPoint: string,
  layout: GPUPipelineLayout | 'auto' = 'auto',
  label?: string,
): GPUComputePipeline {
  const moduleDesc: GPUShaderModuleDescriptor = { code: wgsl };
  if (label) moduleDesc.label = `${label}.wgsl`;
  const desc: GPUComputePipelineDescriptor = {
    layout,
    compute: {
      module: device.createShaderModule(moduleDesc),
      entryPoint,
    },
  };
  if (label) desc.label = label;
  return device.createComputePipeline(desc);
}

export interface RenderPipelineConfig {
  vertexEntry?: string;
  fragmentEntry?: string;
  buffers?: GPUVertexBufferLayout[];
  targets: GPUColorTargetState[];
  depthStencil?: GPUDepthStencilState;
  primitive?: GPUPrimitiveState;
  layout?: GPUPipelineLayout | 'auto';
  label?: string;
}

export function createRenderPipeline(device: GPUDevice, wgsl: string, cfg: RenderPipelineConfig): GPURenderPipeline {
  const moduleDesc: GPUShaderModuleDescriptor = { code: wgsl };
  if (cfg.label) moduleDesc.label = `${cfg.label}.wgsl`;
  const module = device.createShaderModule(moduleDesc);

  const vertex: GPUVertexState = {
    module,
    entryPoint: cfg.vertexEntry ?? 'vs_main',
  };
  if (cfg.buffers) vertex.buffers = cfg.buffers;

  const desc: GPURenderPipelineDescriptor = {
    layout: cfg.layout ?? 'auto',
    vertex,
    fragment: {
      module,
      entryPoint: cfg.fragmentEntry ?? 'fs_main',
      targets: cfg.targets,
    },
    primitive: cfg.primitive ?? { topology: 'triangle-list', cullMode: 'back' },
  };
  if (cfg.depthStencil) desc.depthStencil = cfg.depthStencil;
  if (cfg.label) desc.label = cfg.label;
  return device.createRenderPipeline(desc);
}
